import { zodResolver } from '@hookform/resolvers/zod';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { useEffect, useState, type ReactElement } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { NameField, type FieldServerError } from './NameField';
import { SwitchRow } from './SwitchRow';
import { createCatalogModel } from '../../../api/admin-vehicle-catalog';
import { catalogName } from '../_lib/catalogForms';
import type { UseCatalogWriteResult } from '../_lib/useCatalogWrite';

/**
 * «Добавить модель» (AUT-437): `POST /makes/:id/models`. The answer is a
 * `ModelDto` and the make's `updatedAt` moved — the runner refetches the
 * make before anything else is saved. Aliases are added in the model dialog.
 */
const schema = z.object({
  name: catalogName,
  popular: z.boolean(),
  onSale: z.boolean(),
  legacy: z.boolean(),
});
type Values = z.infer<typeof schema>;
const DEFAULTS: Values = { name: '', popular: false, onSale: true, legacy: false };
const FLAGS = ['popular', 'onSale', 'legacy'] as const;

export interface ModelCreateDialogProps {
  open: boolean;
  makeId: string;
  write: UseCatalogWriteResult;
  onClose: () => void;
}

export const ModelCreateDialog = ({
  open,
  makeId,
  write,
  onClose,
}: ModelCreateDialogProps): ReactElement => {
  const { t } = useTranslation();
  const [serverError, setServerError] = useState<FieldServerError | null>(null);
  const form = useForm<Values>({ resolver: zodResolver(schema), defaultValues: DEFAULTS });

  useEffect(() => {
    if (open) {
      form.reset(DEFAULTS);
      setServerError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const submit = async (v: Values): Promise<void> => {
    const outcome = await write.run(() => createCatalogModel(makeId, v), {
      successKey: 'vehicleCatalog.models.created',
    });
    if (outcome.ok) onClose();
    else if (outcome.error.kind === 'conflict' || outcome.error.kind === 'tooLong') {
      setServerError(outcome.error);
    }
  };

  return (
    <Dialog open={open} onClose={write.busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('vehicleCatalog.modelDialog.createTitle')}</DialogTitle>
      <form noValidate onSubmit={form.handleSubmit(submit)}>
        <DialogContent>
          <Controller
            control={form.control}
            name="name"
            render={({ field, fieldState }) => (
              <NameField
                {...field}
                onChange={(value) => {
                  setServerError(null);
                  field.onChange(value);
                }}
                label={t('vehicleCatalog.name.label')}
                errorKey={fieldState.error?.message}
                serverError={serverError}
                disabled={write.busy}
                focusOnMount
              />
            )}
          />
          {FLAGS.map((flag) => (
            <Controller
              key={flag}
              control={form.control}
              name={flag}
              render={({ field }) => (
                <SwitchRow
                  label={t(`vehicleCatalog.toggles.${flag}`)}
                  hint={t(`vehicleCatalog.toggles.${flag}Hint`)}
                  checked={field.value}
                  disabled={write.busy}
                  onChange={field.onChange}
                />
              )}
            />
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={write.busy}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" variant="contained" disabled={write.busy} loading={write.busy}>
            {t('vehicleCatalog.models.add')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
